import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from './useTenant';
import { toast } from 'sonner';

/**
 * All toggleable features in the POS dashboard.
 * super_admin can disable any of these per tenant to simplify the UI.
 */
export const FEATURE_KEYS = [
  // Caisse features
  'caisse_classique', // Écran de caisse complet (/caisse) — OFF = marchand caisse rapide only
  'caisse_prix_libre',
  'caisse_remise_ligne',
  'caisse_remise_globale',
  'caisse_multi_paiement',
  'caisse_partage_addition',
  'caisse_avoir',
  'caisse_note_frais',
  'caisse_devis',
  'caisse_mise_en_attente',
  'caisse_retour',
  // Boutons contextuels panier
  'caisse_bouton_cuisine', // Cuisine (KOT, envoi simple)
  'caisse_bouton_imprimer_kot', // Imprimer KOT avec picker imprimante (hotfix opt-in)
  'caisse_bouton_table', // Table / plan de salle
  'caisse_bouton_frais_port', // Frais de port manuel
  'caisse_bouton_facture', // Bouton Facture PRO
  'bl_enlevement', // BL : saisie « Pris par » + réf. chantier + enlevé/livré avant création
  'facturation_lekompte', // Facture récapitulative des BL émise dans Lekompte (Factur-X)
  'facturation_bl_auto', // Facture du mois émise automatiquement le dernier jour du mois à 21 h (BL non facturés)
  'avoir_lekompte_auto', // Avoir sur un BL déjà facturé : émis automatiquement dans Lekompte sur cette facture
  'encours_plafond_bloquant', // Bouton Bon livraison bloqué si encours + panier > plafond (dérogation responsable)
  'encours_tiroir_especes', // Règlement d'encours en espèces : ouvre le tiroir-caisse
  // Modes de service (TVA différenciée selon mode pour restaurant)
  'caisse_mode_sur_place', // Sur place (TVA 10% restau)
  'caisse_mode_a_emporter', // À emporter (TVA 5.5% restau)
  'caisse_mode_livraison', // Livraison
  // Payment methods
  'paiement_cheque',
  'paiement_ticket_restaurant',
  'paiement_ticket_cadeau',
  'paiement_encours', // Compte client / encours PRO
  'garde_routes_permissions', // pages interdites par adresse directe selon les permissions
  'impression_alerte_echec', // bandeau persistant + Réimprimer pour les bons cuisine en échec
  'carte_sans_tpe_bloquee', // refuse « carte » sans TPE joignable (caisse rapide, restaurant)
  'formation_checklist', // checklist de prise en main sur l'accueil (formation intégrée lot 1)
  'caisse_affichage_compact', // caisse en portrait haut / bas (TPE, téléphone, tablette debout) — défaut ON, paysage jamais concerné
  'aide_videos', // boutons « Voir le tuto » (vidéos 1 min) sur les écrans — défaut ON, OFF = masqués
  'paiement_carte_externe', // TPE bancaire externe (sans myPOS BT) — caissière saisit manuellement sur son TPE
  'vente_au_poids', // Vente au poids : fiche article « vendu au poids » + balance reliée à la caisse
  'monnayeur_automatique', // Monnayeur automatique (cash recycler Cashmatic/VNE) — espèces encaissées par la machine
  'visionpos_tpe', // VisionPOS TPE (Stripe Terminal, ÉTAPE 1 = MODE TEST) — bouton « Carte · VisionPOS TPE »
  'visionpos_softpos', // SoftPOS : la caisse encaisse en sans contact (Stripe Tap to Pay) — tuile « Sans contact · sur cette caisse »
  'visionpos_tpe_live', // VisionPOS TPE / SoftPOS en RÉEL (clés live Linkvision) ; OFF = mode test
  'retour_tpe_detaille', // Retour TPE : n° d'autorisation + 4 chiffres (CB EXT), données myPOS/Stripe gardées, ligne CB sur le ticket
  'ean_base_partagee', // Base de codes-barres partagée : noms proposés et retenus entre commerces
  'articles_affichage', // Articles et stocks : affichage liste ou images, catégorie créée depuis les stocks
  'codes_barres_multiples', // Rattacher un EAN garde l'ancien code : les étiquettes déjà collées scannent encore
  'paiements_tickets_masques', // Tickets désactivés retirés aussi du paiement fractionné, partagé et de la caisse rapide
  'maj_sans_coupure', // Une mise en ligne ne recharge pas la caisse pendant une vente
  'menu_tactile', // Menu ☰ des écrans étroits : une rubrique ouverte, grandes zones d'appui, raccourci Mode Stock
  'mode_stock_demarrage', // PDA : le Mode Stock s'ouvre au démarrage à la place du tableau de bord
  'mode_stock', // Portable hors caisse : le scan ouvre l'article, mise à jour du stock et inventaire guidé
  'ean_serveur', // Base EAN étendue sur le serveur Linkvision (Europe) : noms à vérifier par le commerçant
  'tarifs_famille', // Tarifs Professionnel / Grossiste appliqués en caisse selon la famille du client
  'devis_lekompte', // Devis créés dans Lekompte, repris en caisse et dans le menu
  'facture_lekompte', // Bouton Facture de la caisse : facture Lekompte au lieu de la facture A4 locale
  'tva_liste', // Taux de TVA des articles choisi dans une liste (plus de saisie libre)
  'bl_reglement_choix', // Après un BL : pas de ticket, le client règle maintenant (facture) ou plus tard (fin de mois)
  'etiquettes_file', // File d'étiquettes remplie au PDA, imprimée depuis la page Étiquettes
  'etiquettes_codes_barres', // Étiquettes avec vrai code-barres scannable (EAN-13/Code 128, planches Avery + rouleaux)
  // Dashboard widgets
  'dashboard_stats',
  'dashboard_top_produits',
  'dashboard_graphiques',
  // Client features
  'fiche_client_caisse',
  'points_fidelite',
  // Impression
  'impression_auto',
  'impression_cuisine',
  // Divers
  'tiroir_caisse',
  'mode_screensaver',
  'tpe_integration',
  // Tablette serveur (CHR)
  'chr_cuissons', // Cuissons + précisions structurées sur la prise de commande (lot 1 CHR)
  'chr_kds_agregats', // Bandeau cumuls temps réel sur l'écran cuisine (lot 3 CHR)
  'chr_par_convive', // Saisie par convive en mode "convive actif" (lot 2 CHR)
  'chr_kot_idempotence', // Idempotence persistante des bons KOT via kot_print_jobs (lot 4 CHR)
  'chr_liberer_table_apres_caisse', // Proposer de libérer la table après l'envoi de l'addition en caisse
  'chr_kot_picker', // Choix manuel de l'imprimante à chaque envoi cuisine (dépannage) — sinon routage auto
  // Sécurité
  'securite_pin_config', // PIN Configuration strict : plus de code par défaut 69120, 5 essais puis blocage 15 min
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

/**
 * Feature flags qui défaut à **FALSE** (opt-in explicite, sans row côté DB).
 *
 * La plupart des flags défaut TRUE (legacy permissif) — la caissière a accès
 * à toutes les fonctions tant que l'admin n'a pas désactivé. Mais certaines
 * fonctionnalités altèrent le flux de paiement (ex. paiement carte sur TPE
 * bancaire externe au lieu de myPOS BT) et doivent être activées
 * explicitement par marchand. Le défaut sûr est FALSE — sans cet opt-in,
 * tous les tenants verraient la dialog TPE externe au clic Carte alors
 * qu'ils encaissent via myPOS BT → cassure prod massive.
 */
const OPT_IN_FLAGS = new Set<FeatureKey>([
  'impression_alerte_echec',
  'garde_routes_permissions',
  'carte_sans_tpe_bloquee',
  'formation_checklist',
  'paiement_carte_externe',
  'vente_au_poids',
  'monnayeur_automatique',
  'visionpos_tpe',
  'visionpos_softpos',
  'visionpos_tpe_live',
  'retour_tpe_detaille',
  'caisse_partage_addition',
  'caisse_bouton_imprimer_kot',
  'chr_cuissons',
  'chr_kds_agregats',
  'chr_par_convive',
  'chr_kot_idempotence',
  'chr_kot_picker',
  'chr_liberer_table_apres_caisse',
  'securite_pin_config',
  'etiquettes_codes_barres',
  'etiquettes_file',
  'tva_liste',
  'bl_reglement_choix',
  'facture_lekompte',
  'devis_lekompte',
  'tarifs_famille',
  'ean_base_partagee',
  'ean_serveur',
  'paiements_tickets_masques',
  'maj_sans_coupure',
  'mode_stock',
  'mode_stock_demarrage',
  'menu_tactile',
  'codes_barres_multiples',
  'articles_affichage',
  'bl_enlevement',
  'facturation_lekompte',
  'facturation_bl_auto',
  'avoir_lekompte_auto',
  'encours_plafond_bloquant',
  'encours_tiroir_especes',
]);

/**
 * Fonction pure : résout l'état activé d'un flag à partir d'une Map de toggles.
 *
 * Extraite du hook pour testabilité (sans React Query / Supabase).
 */
export function computeFeatureEnabled(
  toggles: Record<string, boolean> | undefined,
  key: FeatureKey,
): boolean {
  const explicit = toggles?.[key];
  if (explicit === true) return true;
  if (explicit === false) return false;
  // Pas de row pour ce flag (ou data pas encore chargée) :
  //  - flags opt-in (OPT_IN_FLAGS) → défaut FALSE (sûr)
  //  - autres flags → défaut TRUE (legacy permissif)
  return !OPT_IN_FLAGS.has(key);
}

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  caisse_classique: 'Écran de caisse classique',
  caisse_prix_libre: 'Prix libre / Divers',
  caisse_remise_ligne: 'Remise par ligne',
  caisse_remise_globale: 'Remise globale',
  caisse_multi_paiement: 'Paiement mixte / multi-modes',
  caisse_partage_addition: "Partager l'addition (entre convives)",
  caisse_avoir: 'Avoirs',
  caisse_note_frais: 'Notes de frais',
  caisse_devis: 'Devis',
  caisse_mise_en_attente: 'Mise en attente de ticket',
  caisse_retour: 'Retours / Remboursements',
  caisse_bouton_cuisine: 'Bouton Cuisine (envoi KOT)',
  caisse_bouton_imprimer_kot: 'Bouton Imprimer KOT (avec picker imprimante)',
  caisse_bouton_table: 'Bouton Table / plan de salle',
  caisse_bouton_frais_port: 'Bouton Frais de port',
  caisse_bouton_facture: 'Bouton Facture PRO',
  bl_enlevement: 'Bon de livraison : « Pris par », référence chantier, enlevé / livré',
  facturation_lekompte: 'Facturation des BL dans Lekompte (facture récapitulative conforme)',
  facturation_bl_auto:
    'Facture du mois automatique (le dernier jour du mois à 21 h, une facture par client pour les BL non facturés)',
  avoir_lekompte_auto:
    'Avoir automatique dans Lekompte : un avoir sur un BL déjà facturé part sur cette facture (seuls les avoirs créés après activation)',
  encours_plafond_bloquant:
    'Plafond de crédit bloquant : Bon livraison refusé si encours + panier dépasse le plafond (dérogation responsable)',
  encours_tiroir_especes: "Règlement d'encours en espèces : ouvrir le tiroir-caisse",
  caisse_mode_sur_place: 'Mode Sur place (TVA 10 % pour restau)',
  caisse_mode_a_emporter: 'Mode À emporter (TVA 5.5 % pour restau)',
  caisse_mode_livraison: 'Mode Livraison',
  paiement_cheque: 'Paiement par chèque',
  paiement_ticket_restaurant: 'Paiement Ticket Restaurant',
  paiement_ticket_cadeau: 'Paiement Carte / Ticket cadeau',
  paiement_encours: 'Paiement Encours / compte client (B2B)',
  garde_routes_permissions: 'Bloquer l’accès direct aux pages non autorisées (permissions)',
  impression_alerte_echec: 'Alerte persistante + Réimprimer si un bon cuisine ne sort pas',
  carte_sans_tpe_bloquee: 'Bloquer le paiement carte si le TPE est injoignable',
  formation_checklist: 'Checklist de prise en main sur l’accueil',
  caisse_affichage_compact:
    'Caisse en portrait : produits en haut, panier en bas (TPE portable, téléphone, tablette debout)',
  aide_videos: 'Boutons « Voir le tuto » (vidéos d’aide d’une minute)',
  paiement_carte_externe: 'Activer TPE externe',
  vente_au_poids: 'Vente au poids (balance)',
  monnayeur_automatique: 'Monnayeur automatique (espèces)',
  visionpos_tpe: 'Carte · VisionPOS TPE',
  visionpos_softpos: 'Sans contact · sur cette caisse',
  visionpos_tpe_live: 'VisionPOS TPE · encaissement réel (sinon test)',
  retour_tpe_detaille: 'Données carte du TPE (autorisation, 4 chiffres, ligne CB sur le ticket)',
  dashboard_stats: 'Statistiques tableau de bord',
  dashboard_top_produits: 'Top produits',
  dashboard_graphiques: 'Graphiques de vente',
  fiche_client_caisse: 'Fiche client en caisse',
  points_fidelite: 'Points de fidélité',
  impression_auto: 'Impression automatique ticket',
  impression_cuisine: 'Envoi cuisine (KOT)',
  tiroir_caisse: 'Ouverture tiroir-caisse',
  mode_screensaver: 'Écran de veille vidéo',
  tpe_integration: 'Intégration TPE (myPOS)',
  chr_cuissons: 'Cuissons & précisions structurées (tablette serveur)',
  chr_kds_agregats: "Cumuls temps réel sur l'écran cuisine (KDS)",
  chr_par_convive: 'Saisie par convive — mode convive actif (tablette serveur)',
  chr_kot_idempotence: 'Anti-double bon cuisine (idempotence KOT persistante)',
  chr_kot_picker: "Choix manuel de l'imprimante à chaque envoi cuisine (dépannage)",
  chr_liberer_table_apres_caisse:
    "Proposer de libérer la table après l'envoi de l'addition en caisse",
  ean_base_partagee:
    'Base de codes-barres partagée : le nom donné à un code scanné est proposé aux autres commerces',
  articles_affichage:
    'Articles et stocks : affichage liste ou images comme en caisse, création de catégorie depuis les stocks',
  codes_barres_multiples:
    "Plusieurs codes par article : rattacher un code-barres garde l'ancien, les étiquettes déjà collées scannent encore",
  paiements_tickets_masques:
    'Tickets resto et cadeau désactivés : retirés aussi du paiement fractionné, du partage et de la caisse rapide',
  maj_sans_coupure:
    'Mise à jour sans coupure : une nouvelle version attend la fin de la vente en cours pour recharger la caisse',
  menu_tactile:
    "Menu ☰ sur PDA et téléphone : une seule rubrique ouverte, grandes zones d'appui, raccourci Mode Stock",
  mode_stock_demarrage:
    "PDA : le Mode Stock s'ouvre au démarrage à la place du tableau de bord (écran étroit seulement)",
  mode_stock:
    "Mode Stock sur portable : le scan ouvre l'article, mise à jour du stock et inventaire guidé validé par le serveur",
  ean_serveur: 'Base de codes-barres étendue (Europe) : noms proposés, à vérifier avant de valider',
  tarifs_famille: 'Tarifs Professionnel / Grossiste appliqués en caisse selon la famille du client',
  etiquettes_codes_barres:
    "Étiquettes avec vrai code-barres scannable (planches Avery A4 et imprimante d'étiquettes)",
  devis_lekompte:
    'Devis créés depuis la caisse ou dans Lekompte : bouton Devis en caisse et page dans le menu pour les mettre en caisse',
  facture_lekompte:
    "Bouton Facture en caisse : facture Lekompte immédiate, réglée ou laissée à l'encours, au lieu de la facture A4 locale.",
  tva_liste: 'Taux de TVA des articles choisi dans une liste',
  bl_reglement_choix:
    'Après un BL : pas de page de ticket, le client règle maintenant (facture immédiate) ou plus tard (facture Lekompte de fin de mois)',
  etiquettes_file:
    "File d'étiquettes : ajout depuis le Mode Stock, prix modifié au PDA ajouté d'office, impression depuis la page Étiquettes",
  securite_pin_config:
    'Code Configuration strict (fin du code par défaut, essais limités, le gérant crée son code)',
};

export const FEATURE_CATEGORIES: Record<string, FeatureKey[]> = {
  Caisse: [
    'vente_au_poids',
    'caisse_classique',
    'caisse_prix_libre',
    'tarifs_famille',
    'ean_base_partagee',
    'ean_serveur',
    'paiements_tickets_masques',
    'maj_sans_coupure',
    'mode_stock',
    'mode_stock_demarrage',
    'menu_tactile',
    'codes_barres_multiples',
    'caisse_remise_ligne',
    'caisse_remise_globale',
    'caisse_multi_paiement',
    'caisse_partage_addition',
    'caisse_avoir',
    'caisse_note_frais',
    'caisse_devis',
    'caisse_mise_en_attente',
    'caisse_retour',
  ],
  'Boutons panier contextuels': [
    'caisse_bouton_cuisine',
    'caisse_bouton_imprimer_kot',
    'caisse_bouton_table',
    'caisse_bouton_frais_port',
    'caisse_bouton_facture',
    'bl_enlevement',
    'facturation_lekompte',
    'facturation_bl_auto',
    'avoir_lekompte_auto',
    'encours_plafond_bloquant',
    'encours_tiroir_especes',
  ],
  'Modes de service (TVA différenciée)': [
    'caisse_mode_sur_place',
    'caisse_mode_a_emporter',
    'caisse_mode_livraison',
  ],
  'Moyens de paiement': [
    'paiement_cheque',
    'paiement_ticket_restaurant',
    'paiement_ticket_cadeau',
    'paiement_encours',
    'paiement_carte_externe',
    'carte_sans_tpe_bloquee',
    'monnayeur_automatique',
    'visionpos_tpe',
    'visionpos_softpos',
    'visionpos_tpe_live',
    'retour_tpe_detaille',
  ],
  'Tableau de bord': ['dashboard_stats', 'dashboard_top_produits', 'dashboard_graphiques'],
  'Clients & Fidélité': ['fiche_client_caisse', 'points_fidelite'],
  'Impression & Matériel': [
    'impression_alerte_echec',
    'impression_auto',
    'impression_cuisine',
    'tiroir_caisse',
    'tpe_integration',
    'etiquettes_codes_barres',
    'etiquettes_file',
    'tva_liste',
    'bl_reglement_choix',
    'facture_lekompte',
    'devis_lekompte',
  ],
  Sécurité: ['garde_routes_permissions', 'securite_pin_config'],
  Interface: [
    'mode_screensaver',
    'formation_checklist',
    'aide_videos',
    'caisse_affichage_compact',
    'articles_affichage',
  ],
  'Tablette serveur (CHR)': [
    'chr_cuissons',
    'chr_kds_agregats',
    'chr_par_convive',
    'chr_kot_idempotence',
    'chr_kot_picker',
    'chr_liberer_table_apres_caisse',
  ],
};

/**
 * Flags "morts" : définis mais SANS aucune feature câblée derrière (audit
 * 2026-06-16). On les masque des panneaux admin pour ne pas exposer des toggles
 * sans effet. Conservés dans FEATURE_KEYS (rows DB existantes valides) — à
 * retirer / implémenter ultérieurement. NE PAS y mettre un flag réellement câblé.
 */
export const DEPRECATED_FEATURES = new Set<FeatureKey>([
  'caisse_remise_ligne',
  'caisse_avoir',
  'caisse_note_frais',
  'caisse_devis',
  'caisse_retour',
  'dashboard_stats',
  'dashboard_top_produits',
  'dashboard_graphiques',
  'impression_auto',
  'mode_screensaver',
]);

/**
 * Fetch all feature toggles for a tenant.
 * Returns a Map: featureKey → enabled (defaults to true if no row)
 */
export function useFeatureToggles() {
  const tenantId = useEffectiveTenantId();

  const query = useQuery({
    queryKey: ['feature_toggles', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_feature_toggles' as any)
        .select('feature_key, enabled')
        .eq('tenant_id', tenantId!);
      // Gracefully handle missing table — feature not yet deployed
      if (error) {
        if (
          error.code === '42P01' ||
          error.code?.startsWith('PGRST') ||
          error.message?.includes('does not exist') ||
          error.message?.includes('schema cache')
        )
          return {};
        throw error;
      }
      const map: Record<string, boolean> = {};
      (data as any[])?.forEach((row: any) => {
        map[row.feature_key] = row.enabled;
      });
      return map;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    toggles: query.data || {},
    isLoading: query.isLoading,
    // Faux tant que le commerce n'est pas connu, contrairement à isLoading.
    isFetched: query.isFetched,
    isFeatureEnabled: (key: FeatureKey): boolean => computeFeatureEnabled(query.data, key),
  };
}

/**
 * Mutation to set feature toggles for a given tenant (super_admin only)
 */
export function useSetFeatureToggles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      features,
    }: {
      tenantId: string;
      features: Record<string, boolean>;
    }) => {
      // Upsert each feature toggle
      const rows = Object.entries(features).map(([key, enabled]) => ({
        tenant_id: tenantId,
        feature_key: key,
        enabled,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('tenant_feature_toggles' as any)
        .upsert(rows, { onConflict: 'tenant_id,feature_key' });

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feature_toggles', variables.tenantId] });
      toast.success('Fonctionnalités mises à jour');
    },
    onError: (err: any) => {
      toast.error(`Erreur: ${err.message}`);
    },
  });
}
